import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtPct, CR, L } from "../lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Area, ComposedChart, BarChart, Bar, Cell } from "recharts";
import { RotateCcw, Zap, Save, TrendingUp } from "lucide-react";

const AMBER = "#fb8b1e";
const POS = "#4af6c3";
const NEG = "#ff433d";
const NEU = "#0068ff";

const BASELINE = {
    turnover: 15.5,       // ₹ Cr annual
    growth_pct: 25,       // % QoQ growth to next 4 quarters
    gross_margin: 18.6,   // %
    ar_days: 68,          // days
    tender_win_rate: 42,  // %
    tender_pipeline: 287, // ₹ Cr
    debt_rate: 10.9,      // weighted avg %
    cost_inflation: 6,    // %
    new_capex: 0,         // ₹ Cr
};

const CONTROLS = [
    { key: "growth_pct", label: "QoQ Growth Rate", min: 0, max: 100, step: 1, unit: "%", tip: "Compound quarter-on-quarter revenue growth" },
    { key: "gross_margin", label: "Gross Margin", min: 10, max: 25, step: 0.1, unit: "%", tip: "Direct project margin after materials, labour, subcontractors" },
    { key: "ar_days", label: "AR Collection Days", min: 30, max: 120, step: 1, unit: "d", tip: "Average receivable collection period" },
    { key: "tender_win_rate", label: "Tender Win Rate", min: 15, max: 80, step: 1, unit: "%", tip: "Success rate on submitted bids" },
    { key: "debt_rate", label: "Weighted Debt Rate", min: 7.5, max: 14, step: 0.05, unit: "%", tip: "Effective interest cost on outstanding loans" },
    { key: "cost_inflation", label: "Cost Inflation", min: -2, max: 15, step: 0.5, unit: "%", tip: "YoY inflation in materials/labour costs" },
    { key: "new_capex", label: "Growth CAPEX", min: 0, max: 20, step: 0.5, unit: "Cr", tip: "New equipment / capacity investment" },
];

function simulate(inputs) {
    // 8 quarters forward projection
    const quarters = ["Q1 27", "Q2 27", "Q3 27", "Q4 27", "Q1 28", "Q2 28", "Q3 28", "Q4 28"];
    let rev = BASELINE.turnover / 4;  // per-quarter starting
    const growthFactor = 1 + inputs.growth_pct / 100;
    const marginFactor = inputs.gross_margin / 100;
    const netFactor = marginFactor - 0.032 - (inputs.cost_inflation / 100) * 0.35 - (inputs.debt_rate - 10.9) / 100 * 0.10;
    const debtCost = inputs.debt_rate / 100 / 4;  // per quarter
    let cash = 0.68;  // ₹ Cr - starting
    let debt = 4.92;
    const series = [];
    let cumRevenue = 0;
    let cumProfit = 0;

    for (let i = 0; i < quarters.length; i++) {
        rev = rev * growthFactor;
        const gp = rev * marginFactor;
        const net = rev * Math.max(0.005, netFactor);
        const arDrag = rev * (inputs.ar_days / 90);   // more days = more locked
        const cashDelta = net - inputs.new_capex / 8 - debt * debtCost;
        cash = cash + cashDelta;
        cumRevenue += rev;
        cumProfit += net;
        series.push({
            quarter: quarters[i],
            revenue: rev,
            gross_profit: gp,
            net_profit: net,
            cash: cash,
            debt: debt,
            cumRevenue: cumRevenue,
            arLocked: arDrag,
        });
    }
    const tenderExpected = BASELINE.tender_pipeline * inputs.tender_win_rate / 100;
    const annual_run = series[3].revenue * 4;  // Q4 annualized
    const reach_100 = series.findIndex((s) => (s.cumRevenue + BASELINE.turnover) >= 100);

    return {
        series,
        headline: {
            annual_run,
            cumRevenue: cumRevenue + BASELINE.turnover,
            cumProfit,
            end_cash: cash,
            end_debt: debt,
            tender_expected_win: tenderExpected,
            months_to_100cr: reach_100 >= 0 ? (reach_100 + 1) * 3 : 24,
        },
    };
}

export default function Simulator() {
    const [inputs, setInputs] = useState(BASELINE);
    const [baselineSim, setBaselineSim] = useState(null);
    const [saved, setSaved] = useState([]);

    useEffect(() => { setBaselineSim(simulate(BASELINE)); }, []);

    const sim = useMemo(() => simulate(inputs), [inputs]);
    const dirty = Object.keys(CONTROLS.reduce((a, c) => ({ ...a, [c.key]: 1 }), {})).some(k => inputs[k] !== BASELINE[k]);

    const reset = () => setInputs(BASELINE);

    const saveScenario = () => {
        const name = prompt("Name this scenario", `Scenario ${saved.length + 1}`);
        if (!name) return;
        setSaved([...saved, { name, inputs: { ...inputs }, sim, saved_at: new Date().toISOString() }]);
    };

    // Chart data - compare baseline vs scenario
    const chartData = sim.series.map((s, i) => ({
        quarter: s.quarter,
        Scenario: s.revenue,
        Baseline: baselineSim?.series[i]?.revenue || 0,
        Target_100Cr: 100 / 4,
    }));

    const cashData = sim.series.map((s, i) => ({
        quarter: s.quarter,
        cash: s.cash,
        baseline_cash: baselineSim?.series[i]?.cash || 0,
    }));

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// STRATEGIC WHAT-IF SIMULATOR</div>
                    <h1 className="font-display font-black text-3xl">Move Levers · See Impact</h1>
                    <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">8-quarter forward projection · Slide any lever → live re-calc</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="sim-reset"
                        onClick={reset}
                        disabled={!dirty}
                        className="border border-panel px-3 py-2 text-[10px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] transition-colors disabled:opacity-40 flex items-center gap-1.5"
                    >
                        <RotateCcw size={12} /> Reset
                    </button>
                    <button
                        data-testid="sim-save"
                        onClick={saveScenario}
                        className="border border-[var(--brand-amber)] bg-[var(--brand-amber)] text-black px-3 py-2 text-[10px] font-mono-fin uppercase tracking-wider flex items-center gap-1.5"
                    >
                        <Save size={12} /> Save Scenario
                    </button>
                </div>
            </div>

            {/* Live headline KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard testId="sim-annual" label="Annualised (Q4 28)" value={sim.headline.annual_run * CR} sub={`Baseline: ${(baselineSim?.headline.annual_run || 0).toFixed(1)} Cr`} accent />
                <KpiCard testId="sim-cumrev" label="Cum 8Q Revenue" value={sim.headline.cumRevenue * CR} sub={`Target ₹100 Cr`} />
                <KpiCard testId="sim-cumprofit" label="Cum Net Profit" value={sim.headline.cumProfit * CR} sub="Total 8Q" />
                <KpiCard testId="sim-endcash" label="End Cash (Q4 28)" value={sim.headline.end_cash * CR} sub={sim.headline.end_cash < 0 ? "⚠️ Cash-negative" : "Positive"} />
                <KpiCard testId="sim-tender" label="Expected Tender Wins" value={sim.headline.tender_expected_win * CR} sub={`${inputs.tender_win_rate}% × ₹287 Cr`} />
                <KpiCard testId="sim-time100" label="Time to ₹100 Cr" value={`${sim.headline.months_to_100cr}m`} format="custom" sub={sim.headline.months_to_100cr <= 18 ? "Target hit ✓" : "Behind target"} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {/* Sliders */}
                <Panel testId="sim-controls" title="Levers" subtitle="Adjust to explore">
                    <div className="space-y-4">
                        {CONTROLS.map((c) => (
                            <div key={c.key} data-testid={`slider-${c.key}`}>
                                <div className="flex justify-between items-baseline mb-1.5">
                                    <label className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)]">{c.label}</label>
                                    <span className={`font-mono-fin text-sm ${inputs[c.key] !== BASELINE[c.key] ? "text-[var(--brand-amber)]" : "text-[var(--text-primary)]"}`}>
                                        {typeof inputs[c.key] === "number" ? inputs[c.key].toFixed(c.step < 1 ? 1 : 0) : inputs[c.key]}{c.unit}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={c.min}
                                    max={c.max}
                                    step={c.step}
                                    value={inputs[c.key]}
                                    onChange={(e) => setInputs({ ...inputs, [c.key]: parseFloat(e.target.value) })}
                                    className="w-full accent-[var(--brand-amber)] h-1 bg-[var(--bg-base)]"
                                />
                                <div className="flex justify-between text-[9px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider mt-1">
                                    <span>{c.min}{c.unit}</span>
                                    <span title={c.tip} className="cursor-help">{c.tip.slice(0, 40)}{c.tip.length > 40 ? "..." : ""}</span>
                                    <span>{c.max}{c.unit}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>

                {/* Revenue trajectory */}
                <Panel testId="sim-revenue-chart" className="xl:col-span-2" title="Revenue Trajectory · Scenario vs Baseline">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid stroke="#E2E8F0" strokeOpacity={0.4} vertical={false} />
                                <XAxis dataKey="quarter" tick={{ fill: "#8892B0", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#8892B0", fontSize: 10 }} unit=" Cr" />
                                <Tooltip formatter={(v) => `₹${v.toFixed(2)} Cr`} />
                                <Legend />
                                <Line type="monotone" dataKey="Baseline" stroke="#475569" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                                <Line type="monotone" dataKey="Scenario" stroke={AMBER} strokeWidth={2.5} dot={{ fill: AMBER, r: 3 }} />
                                <Line type="monotone" dataKey="Target_100Cr" stroke={POS} strokeWidth={1.5} strokeDasharray="2 4" dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                <Panel testId="sim-cash" title="Cash Position Trajectory">
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={cashData}>
                                <CartesianGrid stroke="#E2E8F0" strokeOpacity={0.4} vertical={false} />
                                <XAxis dataKey="quarter" tick={{ fill: "#8892B0", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#8892B0", fontSize: 10 }} unit=" Cr" />
                                <Tooltip formatter={(v) => `₹${v.toFixed(2)} Cr`} />
                                <Legend />
                                <Line type="monotone" dataKey="baseline_cash" name="Baseline" stroke="#475569" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                                <Line type="monotone" dataKey="cash" name="Scenario" stroke={sim.series[7].cash > 0 ? POS : NEG} strokeWidth={2.5} dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel testId="sim-scenarios" title="Saved Scenarios" subtitle={`${saved.length} snapshot${saved.length !== 1 ? "s" : ""}`}>
                    {saved.length === 0 ? (
                        <div className="text-xs font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider py-8 text-center">
                            No saved scenarios yet.<br />
                            <span className="text-[var(--text-muted)] normal-case">Save current adjustments to compare later.</span>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {saved.map((s, i) => (
                                <div key={i} className="border border-panel p-3 hover:bg-[var(--bg-panel-hover)]">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-xs text-[var(--text-primary)] font-display font-bold">{s.name}</div>
                                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                                                Annual: ₹{s.sim.headline.annual_run.toFixed(1)} Cr · Cash: ₹{s.sim.headline.end_cash.toFixed(2)} Cr · Time to 100: {s.sim.headline.months_to_100cr}m
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setInputs(s.inputs)}
                                            className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline"
                                        >
                                            Load →
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>

            {/* Insight bar */}
            <Panel testId="sim-insight" title="Insight" subtitle="Auto-generated from your scenario">
                <div className="flex items-start gap-3">
                    <TrendingUp size={18} className="text-[var(--brand-amber)] mt-1" />
                    <div className="text-sm text-[var(--text-primary)] font-body leading-relaxed">
                        At <span className="text-amber font-medium">{inputs.growth_pct.toFixed(0)}% QoQ growth</span>, <span className="text-amber font-medium">{inputs.gross_margin.toFixed(1)}% gross margin</span>, and <span className="text-amber font-medium">{inputs.tender_win_rate}%</span> tender win rate, A W Constructions reaches <span className="text-pos font-medium">₹{sim.headline.annual_run.toFixed(1)} Cr</span> annualised turnover by Q4 FY28, with cumulative 8-quarter revenue of <span className="text-pos font-medium">₹{sim.headline.cumRevenue.toFixed(1)} Cr</span>.
                        {" "}
                        {sim.headline.end_cash < 0 && <span className="text-neg">⚠️ Cash goes negative — need working-capital funding.</span>}
                        {sim.headline.months_to_100cr <= 18 && <span className="text-pos"> ✓ Hits ₹100 Cr milestone in {sim.headline.months_to_100cr} months — target achievable.</span>}
                        {sim.headline.months_to_100cr > 18 && sim.headline.months_to_100cr <= 24 && <span className="text-amber"> ⚠️ Slightly behind — needs {(18 / sim.headline.months_to_100cr * 100).toFixed(0)}% acceleration.</span>}
                        {sim.headline.months_to_100cr > 24 && <span className="text-neg"> ⚠️ Target beyond horizon — increase growth or margin.</span>}
                    </div>
                </div>
            </Panel>
        </div>
    );
}
